import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { APICallStatus, CreateServiceRequestData, UpdateServiceRequestData } from "../../../Types/UnitFeatureTypes";


interface ServiceRequestRequestState {
  request: {
    loading: boolean;
    success: boolean;
    createServiceRequestAPI: APICallStatus;
    editServiceRequestAPI: APICallStatus;
    error: string | null;
    serviceRequestId: number | null;
  };
}

export enum Types {
  ADD_SERVICE_REQUEST_REQUEST = 'ADD_SERVICE_REQUEST_REQUEST',
  ADD_SERVICE_REQUEST_SUCCESS = 'ADD_SERVICE_REQUEST_SUCCESS',
  ADD_SERVICE_REQUEST_FAILURE = 'ADD_SERVICE_REQUEST_FAILURE',
  EDIT_SERVICE_REQUEST_REQUEST = 'EDIT_SERVICE_REQUEST_REQUEST',
  EDIT_SERVICE_REQUEST_SUCCESS = 'EDIT_SERVICE_REQUEST_SUCCESS',
  EDIT_SERVICE_REQUEST_FAILURE = 'EDIT_SERVICE_REQUEST_FAILURE',
  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const addServiceRequestAction = createAsyncAction(
  Types.ADD_SERVICE_REQUEST_REQUEST,
  Types.ADD_SERVICE_REQUEST_SUCCESS,
  Types.ADD_SERVICE_REQUEST_FAILURE,
)
<
  { serviceRequestData: CreateServiceRequestData, },
  { serviceRequestId: number },
  { message: string }
>();

const editServiceRequestAction = createAsyncAction(
  Types.EDIT_SERVICE_REQUEST_REQUEST,
  Types.EDIT_SERVICE_REQUEST_SUCCESS,
  Types.EDIT_SERVICE_REQUEST_FAILURE,
)
<
  { serviceRequestData: UpdateServiceRequestData, id: string },
  void,
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { addServiceRequestAction, editServiceRequestAction, resetStatusRequestAction };

export const initialState: ServiceRequestRequestState = {
  request: {
    loading: false,
    error: null as string | null,
    createServiceRequestAPI: APICallStatus.PENDING,
    editServiceRequestAPI: APICallStatus.PENDING,
    success: false,
    serviceRequestId: null,
  }
};

export type ServiceRequestState = typeof initialState;

const addServiceRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;
    draft.request.createServiceRequestAPI = APICallStatus.PENDING;

    return draft;
  });

const addServiceRequestSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.addServiceRequestAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    draft.request.createServiceRequestAPI = APICallStatus.SUCCESS;
    if (draft.request.success) {
      draft.request.loading = false;
      draft.request.serviceRequestId = payload.serviceRequestId;
    }

    return draft;
  });

const addServiceRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.addServiceRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.createServiceRequestAPI = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

const editServiceRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.editServiceRequestAPI = APICallStatus.PENDING;
    draft.request.error = null;

    return draft;
  });

const editServiceRequestSuccess = (
  state = initialState
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    draft.request.editServiceRequestAPI = APICallStatus.SUCCESS
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const editServiceRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.editServiceRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
    draft.request.editServiceRequestAPI = APICallStatus.FAILURE;

    return draft;
  });

const resetStatusRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = null;
    draft.request.success = false;
    draft.request.createServiceRequestAPI = APICallStatus.PENDING;
    draft.request.editServiceRequestAPI = APICallStatus.PENDING;

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.addServiceRequestAction.request, addServiceRequestRequest)
  .handleAction(Actions.addServiceRequestAction.success, addServiceRequestSuccess)
  .handleAction(Actions.addServiceRequestAction.failure, addServiceRequestFailure)
  .handleAction(Actions.editServiceRequestAction.request, editServiceRequestRequest)
  .handleAction(Actions.editServiceRequestAction.success, editServiceRequestSuccess)
  .handleAction(Actions.editServiceRequestAction.failure, editServiceRequestFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  